import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, RouterButtonLink } from "../components/Button";
import { Card } from "../components/Card";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Container } from "../components/Container";
import { FormActions, FormActionsStart } from "../components/FormActions";
import { InfoBox } from "../components/InfoBox";
import { LoadingContainer } from "../components/LoadingContainer";
import { PageDetailHeader } from "../components/PageDetailHeader";
import { useCatalogScope } from "../hooks/useCatalogScope";
import { CATALOG_NEW_ITEM_ID, useCatalogNav } from "./catalog/catalogNav";
import { OfferForm } from "./components/offers/OfferForm";
import {
  useGetOfferQuery,
  useCreateOfferMutation,
  useUpdateOfferMutation,
  useDeleteOfferMutation,
} from "../query/offersQuery";
import type { RestCreateOffer } from "@KassaroAB/shared/types/offer";

const FORM_ID = "catalog-offer-form";

export const OfferFormView = () => {
  const { t } = useTranslation();
  const { setDirty, allowNavigate } = useCatalogNav();
  const { scope, pagePath } = useCatalogScope("offers");
  const { itemId } = useParams<{ itemId: string }>();
  const isCreating = itemId === CATALOG_NEW_ITEM_ID;
  const offerId = !isCreating && itemId ? itemId : null;

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [savedTick, setSavedTick] = useState(0);

  const {
    data: offer,
    isLoading,
    error: loadError,
  } = useGetOfferQuery(scope, offerId);
  const {
    mutate: createMutation,
    error: createError,
    isPending: isCreatePending,
  } = useCreateOfferMutation(scope);
  const {
    mutate: updateMutation,
    error: updateError,
    isPending: isUpdatePending,
  } = useUpdateOfferMutation(scope);
  const {
    mutate: deleteMutation,
    error: deleteError,
    isPending: isDeletePending,
  } = useDeleteOfferMutation(scope);

  const isSaving = isCreatePending || isUpdatePending;
  const missing = !isCreating && !!offerId && !isLoading && !offer;

  const handleSubmit = (data: RestCreateOffer) => {
    if (isCreating) {
      createMutation(data, {
        onSuccess: (created) => {
          allowNavigate(`${pagePath}/${created.id}`);
        },
      });
      return;
    }
    if (!offerId) return;
    updateMutation(
      { offerId, offer: data },
      {
        onSuccess: () => {
          setSuccessMessage(t("offersView.successUpdated"));
          setSavedTick((tick) => tick + 1);
        },
        onError: () => setSuccessMessage(null),
      },
    );
  };

  const confirmDelete = () => {
    if (!offerId) return;
    deleteMutation(offerId, {
      onSuccess: () => {
        setDeleteOpen(false);
        allowNavigate(pagePath);
      },
    });
  };

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  const title = isCreating
    ? t("offersView.panelCreate")
    : (offer?.name ?? t("offersView.title"));
  const breadcrumbCurrent = isCreating
    ? t("offersView.panelCreate")
    : missing
      ? t("catalog.itemNotFound")
      : (offer?.name ?? t("common.loading"));

  if (!scope) {
    return null;
  }

  const showForm = isCreating || (!!offer && !missing);

  return (
    <Container>
      <PageDetailHeader
        title={title}
        parentLabel={t("offersView.title")}
        parentTo={pagePath}
        currentLabel={breadcrumbCurrent}
      />
      {loadError && (
        <InfoBox variant="error">{t("offersView.errorLoad")}</InfoBox>
      )}
      {(createError || updateError || deleteError) && (
        <InfoBox variant="error">{t("offersView.errorSave")}</InfoBox>
      )}
      {successMessage && (
        <InfoBox variant="success">{successMessage}</InfoBox>
      )}
      <Card>
        {!isCreating && isLoading && !offer ? (
          <LoadingContainer message={t("offersView.loading")} />
        ) : missing || !showForm ? (
          <InfoBox variant="error">{t("catalog.itemNotFound")}</InfoBox>
        ) : (
          <>
            <OfferForm
              key={offer?.id ?? CATALOG_NEW_ITEM_ID}
              formId={FORM_ID}
              offer={isCreating ? undefined : offer}
              savedTick={savedTick}
              onSubmit={handleSubmit}
              onDirtyChange={setDirty}
            />
            <FormActions>
              {!isCreating && (
                <FormActionsStart>
                  <Button
                    type="button"
                    $color="danger"
                    onClick={() => setDeleteOpen(true)}
                    disabled={isSaving || isDeletePending}
                  >
                    {t("common.delete")}
                  </Button>
                </FormActionsStart>
              )}
              <RouterButtonLink
                to={pagePath}
                $color="secondary"
                onClick={(event) => {
                  if (isSaving) event.preventDefault();
                }}
              >
                {t("common.cancel")}
              </RouterButtonLink>
              <Button type="submit" form={FORM_ID} disabled={isSaving}>
                {isSaving
                  ? t("common.saving")
                  : isCreating
                    ? t("common.create")
                    : t("common.update")}
              </Button>
            </FormActions>
          </>
        )}
      </Card>
      <ConfirmDialog
        isOpen={deleteOpen}
        title={t("offerForm.deleteOffer")}
        message={t("offersView.deleteConfirm", {
          name: offer?.name ?? "",
        })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        danger
        onConfirm={confirmDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </Container>
  );
};
